/**
 * Kāla Viśeṣa — intra-day time windows (Avoid | Favourable), editorial grammar
 * (docs/DAILY_PANCHANGA_UI_SPEC.md §8.5). Semantic red/green lives HERE (the
 * quality-of-time signal), not in the monochrome five-limbs table. Rendered twice
 * by DailyPanchangaView (desktop + mobile) — one component so copies can't drift.
 */
import React from 'react';
import type { IntradayWindow, Locale } from '../../types/api';
import { uiString } from '../../i18n/uiStrings';

interface KalaViseshaCardProps {
  windows: IntradayWindow[];
  locale?: Locale;
  onLearnMore: () => void;
}

const ROMAN = ['I', 'II', 'III', 'IV'];

// Chronological order + disambiguating ordinals when a window occurs more than
// once in a day (e.g. Durmuhūrta I / II), so repeats don't look like a bug.
function prepare(list: IntradayWindow[]): (IntradayWindow & { label: string })[] {
  const sorted = [...list].sort((a, b) => a.start_time.localeCompare(b.start_time));
  const counts = new Map<string, number>();
  sorted.forEach(w => counts.set(w.name, (counts.get(w.name) || 0) + 1));
  const seen = new Map<string, number>();
  return sorted.map(w => {
    if ((counts.get(w.name) || 0) > 1) {
      const n = seen.get(w.name) || 0;
      seen.set(w.name, n + 1);
      return { ...w, label: `${w.name} ${ROMAN[n] || n + 1}` };
    }
    return { ...w, label: w.name };
  });
}

export default function KalaViseshaCard({ windows, locale = 'en', onLearnMore }: KalaViseshaCardProps) {
  const inauspicious = prepare(windows.filter(w => w.type === 'inauspicious'));
  const auspicious = prepare(windows.filter(w => w.type === 'auspicious'));

  const rows = (list: (IntradayWindow & { label: string })[], nameColor: string) => (
    <div className="space-y-1">
      {list.map((w, idx) => (
        <div key={idx} className="flex items-baseline gap-2 text-[13.5px]">
          <span className={`shrink-0 ${nameColor}`}>{w.label}</span>
          <span className="min-w-[1rem] flex-1 self-center border-b border-dotted" style={{ borderColor: 'var(--kg-line)' }} aria-hidden="true" />
          <span className="shrink-0 tabular-nums whitespace-nowrap" style={{ color: 'var(--kg-sub)' }}>{w.start_time}–{w.end_time}</span>
        </div>
      ))}
    </div>
  );

  return (
    <section className="kg-warm rounded-2xl p-5" style={{ background: 'var(--kg-card)', border: '1px solid var(--kg-line)', boxShadow: 'var(--kg-shadow)' }}>
      <div className="mb-3 flex items-baseline justify-between gap-2">
        <h3 className="font-fraunces text-[18px] font-medium" style={{ color: 'var(--kg-ink)' }}>{uiString(locale, 'kalaVisesha')}</h3>
        <button onClick={onLearnMore} className="kg-link text-[12.5px] font-semibold">
          {uiString(locale, 'howCalculated')}
        </button>
      </div>
      <div className="space-y-3">
        {/* Avoid */}
        <div>
          <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-red-600 dark:text-red-400">{uiString(locale, 'avoid')}</div>
          {rows(inauspicious, 'text-red-700 dark:text-red-400')}
        </div>
        {/* Favourable */}
        <div>
          <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-green-600 dark:text-green-400">{uiString(locale, 'favourable')}</div>
          {rows(auspicious, 'text-green-700 dark:text-green-400')}
        </div>
      </div>
    </section>
  );
}
